// Enhanced Christmas Gift Promo with Exit Intent Popup and 2-Step Form
// This is the integrated version that includes all funnel components

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Gift, ArrowRight } from "lucide-react";
import ChristmasGiftPromo from "./ChristmasGiftPromo";
import HolidayGiftCardMultiStepPopup from "./HolidayGiftCardMultiStepPopup";
import { trackEvent } from "../utils/centerfyTracking";
import { Button } from "./ui/button";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogDescription as DialogDescription,
} from "./ui/responsive-dialog";

/**
 * Wrapper component that adds exit-intent popup and form to Christmas Gift Promo page
 * Supports A/B testing via URL parameter ?variant=a or ?variant=b
 */
export default function ChristmasGiftPromoWithPopup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get variant from URL or randomly assign (50/50 split)
  const getVariant = () => {
    const variantParam = searchParams.get("variant");
    if (variantParam === "a" || variantParam === "b") return variantParam;

    // Random 50/50 split for A/B testing
    // Store in sessionStorage to keep consistent for user session
    const stored = sessionStorage.getItem("holiday-promo-variant");
    if (stored === "a" || stored === "b") return stored;

    const variant = Math.random() < 0.5 ? "a" : "b";
    sessionStorage.setItem("holiday-promo-variant", variant);
    return variant;
  };

  const [variant] = useState(() => getVariant());
  const [showPopup, setShowPopup] = useState(false);
  const [hasTriggeredPopup, setHasTriggeredPopup] = useState(false);
  const [hasClosedPopup, setHasClosedPopup] = useState(false);
  const [showBackNavigationPopup, setShowBackNavigationPopup] = useState(false);

  // Show popup instantly on mount
  useEffect(() => {
    setShowPopup(true);
  }, []);

  // Set up 15-second interval to show popup after closing (only after first close)
  useEffect(() => {
    if (hasClosedPopup && !showPopup) {
      const interval = setInterval(() => {
        setShowPopup(true);
      }, 15000); // 15 seconds

      return () => clearInterval(interval);
    }
  }, [hasClosedPopup, showPopup]);

  // Track popup view for A/B testing
  useEffect(() => {
    if (showPopup && !hasTriggeredPopup) {
      setHasTriggeredPopup(true);
      trackEvent("popup_view", {
        popupId: "exit-intent-holiday-gift",
        variant,
        pagePath: "/gift-cards/christmas",
      });
    }
  }, [showPopup, hasTriggeredPopup, variant]);

  // Handle browser back button navigation
  useEffect(() => {
    // Push a state to history to intercept back button
    window.history.pushState({ preventBack: true }, "", window.location.href);

    const handlePopState = () => {
      // Show the back navigation popup
      setShowBackNavigationPopup(true);

      // Push state again immediately to keep user on the page
      // This prevents the actual navigation
      window.history.pushState({ preventBack: true }, "", window.location.href);
    };

    // Listen for popstate event (back/forward button)
    window.addEventListener("popstate", handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handlePopupClose = () => {
    setShowPopup(false);
    setHasClosedPopup(true);
  };

  const handlePopupSuccess = (paymentData) => {
    // Navigate to thank you page
    navigate("/gift-cards/holiday/thank-you", {
      state: { paymentData, variant },
    });
  };

  const handleStayOnPage = () => {
    setShowBackNavigationPopup(false);
  };

  const handleViewOffer = () => {
    setShowBackNavigationPopup(false);
    setShowPopup(true);
  };

  return (
    <>
      {/* Original Christmas Gift Promo Page */}
      <ChristmasGiftPromo />

      {/* Multi-Step Popup (Offer → Contact → Payment) */}
      <HolidayGiftCardMultiStepPopup
        isOpen={showPopup}
        onClose={handlePopupClose}
        onSuccess={handlePopupSuccess}
        variant={variant}
      />

      {/* Back Navigation Popup */}
      <Dialog
        open={showBackNavigationPopup}
        onOpenChange={setShowBackNavigationPopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-red-500 rounded-full flex items-center justify-center">
                <Gift className="w-8 h-8 text-white" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-center text-gray-900">
              Wait! Don't Miss This Great Deal! 🎁
            </DialogTitle>
            <DialogDescription className="text-center text-base text-gray-600 mt-4">
              You're about to leave an amazing holiday offer behind! Get{" "}
              <span className="font-bold text-green-600">15% off</span> on gift
              cards $100+ with code{" "}
              <span className="font-bold text-red-600">HOLIDAYS</span>. This
              limited-time offer won't last forever!
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-6">
            <Button
              onClick={handleViewOffer}
              className="w-full bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700 text-white text-lg py-6 h-auto shadow-lg hover:shadow-xl transition-all duration-300">
              <Gift className="w-5 h-5 mr-2" />
              View Special Offer
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={handleStayOnPage}
              variant="outline"
              className="w-full border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 text-lg py-6 h-auto transition-all duration-300">
              Stay on Page
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
